import { falAngleDown } from 'shared/@generated/icons/falAngleDown';
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Icon } from 'shared/components/icon';
import { SvgImage } from 'shared/components/svg-image';
import { flags } from 'shared/library/i18n/flags';
import { useLocale } from 'shared/library/i18n/use-locale';

const LocaleSwitcherMenu = lazy(() => import('./locale-switcher-menu.web'));

type LocaleSwitcherProps = {
  align?: 'start' | 'center' | 'end';
  trigger?: ReactNode;
};

export function LocaleSwitcher({ align = 'end', trigger }: LocaleSwitcherProps) {
  const { t } = useTranslation('components');
  const { languageCode } = useLocale();
  const [hasRequestedMenu, setHasRequestedMenu] = useState(false);
  const [open, setOpen] = useState(false);
  const [openedWithKeyboard, setOpenedWithKeyboard] = useState(false);
  const [restoreTriggerFocus, setRestoreTriggerFocus] = useState(false);
  const pendingTriggerRef = useRef<HTMLDivElement>(null);
  const label = t('locale_switcher.change_language');
  const triggerContent = trigger ?? (
    <View
      className={
        'border-brand-brown-dark/15 shadow-brand-brown/5 hover:border-brand-brown-dark/30 hover:shadow-brand-brown/10 max-w-28 cursor-pointer touch-manipulation flex-row items-center rounded-md border bg-white px-2 py-1.5 shadow md:px-3 md:py-2'
      }
    >
      <SvgImage src={flags[languageCode]} alt={languageCode} containerClassName="mr-1.5 size-6" />
      <Icon icon={falAngleDown} className="text-brand-brown-dark/70 size-4" />
    </View>
  );

  function requestOpen(withKeyboard: boolean) {
    setOpenedWithKeyboard(withKeyboard);
    setRestoreTriggerFocus(true);
    setOpen(true);
    setHasRequestedMenu(true);
  }

  useEffect(() => {
    if (hasRequestedMenu) pendingTriggerRef.current?.focus();
  }, [hasRequestedMenu]);

  useEffect(() => {
    if (!hasRequestedMenu || !open) return;

    function dismissOutside(event: Event) {
      const trigger = pendingTriggerRef.current;
      // Once the chunk arrives, Radix owns dismissal and this ref is cleared.
      if (trigger && event.target instanceof Node && !trigger.contains(event.target)) {
        setRestoreTriggerFocus(false);
        setOpen(false);
      }
    }

    // Run before React's trigger handler. Opening replaces that trigger with the
    // Suspense fallback, so its original event target is detached by bubble time.
    document.addEventListener('pointerdown', dismissOutside, true);
    document.addEventListener('focusin', dismissOutside);
    return () => {
      document.removeEventListener('pointerdown', dismissOutside, true);
      document.removeEventListener('focusin', dismissOutside);
    };
  }, [hasRequestedMenu, open]);

  const pendingTrigger = (
    <div
      ref={pendingTriggerRef}
      role="button"
      tabIndex={0}
      aria-label={label}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-busy={hasRequestedMenu}
      data-testid="dropdown-trigger"
      className="flex flex-col"
      onPointerDown={(event) => {
        // Match Radix's trigger, including nested React Native touchable content.
        if (event.button !== 0 || event.ctrlKey) return;
        if (open) setOpen(false);
        else {
          event.preventDefault();
          requestOpen(false);
        }
      }}
      onKeyDown={(event) => {
        if (['Enter', ' ', 'ArrowDown'].includes(event.key)) {
          event.preventDefault();
          if (open && event.key !== 'ArrowDown') setOpen(false);
          else requestOpen(true);
        } else if (event.key === 'Escape') {
          setOpen(false);
        }
      }}
    >
      {triggerContent}
    </div>
  );

  if (!hasRequestedMenu) return pendingTrigger;

  return (
    <Suspense fallback={pendingTrigger}>
      <LocaleSwitcherMenu
        align={align}
        label={label}
        open={open}
        openedWithKeyboard={openedWithKeyboard}
        restoreTriggerFocus={restoreTriggerFocus}
        onOpenChange={(nextOpen) => {
          setOpenedWithKeyboard(false);
          setOpen(nextOpen);
        }}
      >
        {triggerContent}
      </LocaleSwitcherMenu>
    </Suspense>
  );
}
